import React from "react";
import { Shield, ShieldCheck, ShieldAlert, Crown, Users, UserCog, Check, Minus } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";

// Ordered highest -> lowest for readability (matches backend rbac.py ROLE_RANK).
const ROLES = ["super_admin", "partner", "customer_admin", "manager", "analyst", "viewer"];

const ROLE_ICON = {
  super_admin: Crown,
  partner: ShieldAlert,
  customer_admin: ShieldCheck,
  manager: UserCog,
  analyst: Shield,
  viewer: Users,
};

const ROLE_DESC = {
  super_admin: "Global platform admin. Bypasses tenant isolation and ownership; can impersonate any tenant.",
  partner: "Manages their own tenant plus any child tenants (parent_tenant_id chain).",
  customer_admin: "Full admin within their own tenant: users, branding, storage, SSO, audit.",
  manager: "Manages all datasets and dashboards across their tenant. Cannot manage users.",
  analyst: "Creates and edits their own datasets, dashboards and calculated fields.",
  viewer: "Read-only. Sees only content that has been explicitly shared with them.",
};

// Capability matrix. Each row = one action. Cells hold "yes", "own", "tenant", "child", or "no".
// This mirrors what the backend enforces in rbac.py, dashboards.py, datasets.py, media.py, tenants.py.
const CATEGORIES = [
  {
    name: "Platform",
    rows: [
      ["Sign in to the application",                 { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes", viewer: "yes" }],
      ["Access the Admin Console",                   { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no",  analyst: "no",  viewer: "no"  }],
      ["Bypass tenant isolation (cross-tenant read)", { super_admin: "yes", partner: "child", customer_admin: "no", manager: "no", analyst: "no", viewer: "no" }],
    ],
  },
  {
    name: "Users & tenants",
    rows: [
      ["Invite / create users",                       { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Change another user's role",                  { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Reset another user's password",               { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Delete users",                                { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Create child tenants",                        { super_admin: "yes", partner: "yes", customer_admin: "no",  manager: "no", analyst: "no", viewer: "no" }],
    ],
  },
  {
    name: "Datasets",
    rows: [
      ["View datasets",                               { super_admin: "yes", partner: "child", customer_admin: "tenant", manager: "tenant", analyst: "tenant", viewer: "own" }],
      ["Upload / connect a new dataset",              { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes", viewer: "no"  }],
      ["Edit any dataset in the tenant",              { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Delete datasets",                             { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Edit the semantic model / calc measures",     { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Manage row-level security (RLS)",             { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no",  analyst: "no",  viewer: "no"  }],
    ],
  },
  {
    name: "Dashboards & widgets",
    rows: [
      ["View dashboards",                             { super_admin: "yes", partner: "child", customer_admin: "tenant", manager: "tenant", analyst: "tenant", viewer: "own" }],
      ["Create dashboards",                           { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes",  viewer: "no"  }],
      ["Edit any dashboard in the tenant",            { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Delete dashboards",                           { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Publish / share a dashboard publicly",        { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
      ["Add comments & annotations",                  { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes",  viewer: "yes" }],
    ],
  },
  {
    name: "AI & automation",
    rows: [
      ["Use Ask AI",                                  { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes",  viewer: "no"  }],
      ["Use AI dashboard wizard / refine",            { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "yes",  viewer: "no"  }],
      ["Configure AI Gateway policies",               { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no",  analyst: "no",   viewer: "no"  }],
      ["Set up scheduled emails / alerts",            { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "yes", analyst: "own",  viewer: "no"  }],
    ],
  },
  {
    name: "Tenant configuration",
    rows: [
      ["Edit tenant branding",                        { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Configure object storage",                    { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Configure SSO / SAML keys",                   { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["View audit logs",                             { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
      ["Configure the weekly health digest",          { super_admin: "yes", partner: "yes", customer_admin: "yes", manager: "no", analyst: "no", viewer: "no" }],
    ],
  },
];

const CELL_META = {
  yes:    { symbol: <Check className="w-4 h-4" />,   tone: "text-[color:var(--success)]", tip: "Allowed" },
  own:    { symbol: <span className="text-[10px] font-semibold tracking-wide">OWN</span>,     tone: "text-[color:var(--brand-ocean)]", tip: "Allowed only on records they own" },
  tenant: { symbol: <span className="text-[10px] font-semibold tracking-wide">TENANT</span>,  tone: "text-[color:var(--brand-ocean)]", tip: "Allowed on any record in their tenant" },
  child:  { symbol: <span className="text-[10px] font-semibold tracking-wide">CHILD</span>,   tone: "text-[color:var(--brand-mint)]",  tip: "Allowed on their tenant + child tenants" },
  no:     { symbol: <Minus className="w-3.5 h-3.5" />, tone: "text-[color:var(--ink-3)] opacity-60", tip: "Not allowed" },
};

function Cell({ value }) {
  const meta = CELL_META[value] || CELL_META.no;
  return (
    <span title={meta.tip} className={"inline-flex items-center justify-center h-6 min-w-[38px] px-1 rounded-md " + meta.tone}>
      {meta.symbol}
    </span>
  );
}

export function AdminRoles() {
  return (
    <div data-testid="admin-roles" className="space-y-5">
      {/* Role summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {ROLES.map((r) => {
          const Icon = ROLE_ICON[r] || Shield;
          return (
            <div key={r} data-testid={`role-card-${r}`} className="glass-card p-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[color:var(--surface-2)] flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[color:var(--brand-ocean)]" />
                </div>
                <div>
                  <div className="font-display text-sm text-[color:var(--ink)]">{ROLE_LABELS[r]}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] font-mono">{r}</div>
                </div>
              </div>
              <p className="text-xs text-[color:var(--ink-2)] mt-2 leading-relaxed">{ROLE_DESC[r]}</p>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[color:var(--ink-3)]">
        <span className="font-medium text-[color:var(--ink-2)]">Legend:</span>
        <span className="inline-flex items-center gap-1"><Cell value="yes" />Allowed</span>
        <span className="inline-flex items-center gap-1"><Cell value="own" />Only on own records</span>
        <span className="inline-flex items-center gap-1"><Cell value="tenant" />Any record in tenant</span>
        <span className="inline-flex items-center gap-1"><Cell value="child" />Tenant + child tenants</span>
        <span className="inline-flex items-center gap-1"><Cell value="no" />Not allowed</span>
      </div>

      {/* Capability matrix */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="roles-matrix">
            <thead className="bg-[color:var(--surface-2)]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[color:var(--ink-2)] sticky left-0 bg-[color:var(--surface-2)] min-w-[280px]">Capability</th>
                {ROLES.map((r) => (
                  <th key={r} className="px-3 py-3 font-medium text-[color:var(--ink-2)] text-center whitespace-nowrap">
                    <div className="text-xs">{ROLE_LABELS[r]}</div>
                    <div className="text-[9px] text-[color:var(--ink-3)] font-mono normal-case tracking-normal">{r}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((cat) => (
                <React.Fragment key={cat.name}>
                  <tr>
                    <td colSpan={ROLES.length + 1} className="px-4 py-2 text-[11px] uppercase tracking-wider text-[color:var(--ink-3)] font-semibold bg-[color:var(--surface-2)]/40">
                      {cat.name}
                    </td>
                  </tr>
                  {cat.rows.map(([label, perms]) => (
                    <tr key={label} data-testid="role-matrix-row" className="border-t border-[color:var(--glass-border)]">
                      <td className="px-4 py-2.5 text-[color:var(--ink-2)] sticky left-0 bg-[color:var(--surface)]">{label}</td>
                      {ROLES.map((r) => (
                        <td key={r} className="px-3 py-2.5 text-center">
                          <Cell value={perms[r] || "no"} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-[color:var(--ink-3)]">
        Roles and permissions are defined in code (<span className="font-mono">backend/rbac.py</span>) and cannot be edited from the UI. Contact your platform administrator to request a role change.
      </p>
    </div>
  );
}
